import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Upload, FileText, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";

const COLUMNS = [
  "first_name", "last_name", "email", "phone",
  "inquiry_type", "location",
  "requested_date", "start_time", "duration",
  "organization", "notes",
];

function parseCsv(text) {
  // Simple CSV parser — handles double-quoted fields with embedded commas.
  const rows = [];
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.length > 0);
  for (const line of lines) {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i += 1; }
        else if (ch === '"') { inQ = false; }
        else cur += ch;
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ",") { out.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    out.push(cur);
    rows.push(out);
  }
  return rows;
}

export default function LeadsImportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState(null);          // {headers, rows}
  const [brand, setBrand] = useState("rrdc");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const isSuper = user?.role === "super_admin";

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/leads/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "lead_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
    parseAndValidate(text);
  };

  const parseAndValidate = (text) => {
    const all = parseCsv(text);
    if (!all.length) { toast.error("CSV is empty"); setParsed(null); return; }
    const headers = all[0].map((h) => h.trim().toLowerCase());
    const dataRows = all.slice(1)
      .filter((r) => r.some((c) => (c || "").trim().length > 0))
      .map((r) => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (r[i] || "").trim(); });
        return obj;
      });
    setParsed({ headers, rows: dataRows });
  };

  const doImport = async () => {
    if (!parsed?.rows?.length) { toast.error("No rows to import"); return; }
    setImporting(true);
    try {
      const payload = { rows: parsed.rows };
      if (isSuper) payload.brand = brand;
      const { data } = await api.post("/leads/import", payload);
      setResult(data);
      toast.success(`Imported ${data.imported} leads to ${data.brand.toUpperCase()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="leads-import-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/leads")}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-outfit text-2xl font-bold tracking-tight">Import Leads</h1>
            <p className="text-sm text-muted-foreground">Bulk-add leads from a CSV file.</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Step 1 — Get the template</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Download our template, fill it with your leads, then upload it below.
              Required columns: <span className="font-mono">{COLUMNS.join(", ")}</span>.
            </p>
            <p className="text-xs text-muted-foreground">
              <strong>inquiry_type</strong> = <span className="font-mono">rental | birthday_party | event | picnic</span> ·{" "}
              <strong>location</strong> = <span className="font-mono">rrdc_field | rrdc_rink | rrdc_courts | turf_house</span>
            </p>
            <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn">
              <Download size={14} /> Download CSV Template
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Step 2 — Upload your file</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSuper && (
              <div className="space-y-1.5 max-w-sm">
                <label className="text-sm font-medium">Assign these leads to</label>
                <Select value={brand} onValueChange={setBrand}>
                  <SelectTrigger data-testid="import-brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rrdc">Rolling River (RRDC)</SelectItem>
                    <SelectItem value="riverview">Riverview Athletics / Turf House</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">As Super Admin, choose which brand owns these leads.</p>
              </div>
            )}
            {!isSuper && (
              <p className="text-sm text-muted-foreground">
                Leads will be assigned to your brand: <span className="font-bold capitalize">{user?.brand || "—"}</span>.
              </p>
            )}
            <div>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFile}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-input file:bg-background file:text-sm file:font-medium hover:file:bg-accent cursor-pointer"
                data-testid="csv-file-input"
              />
            </div>
            {parsed && (
              <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <FileText size={14} /> {parsed.rows.length} row{parsed.rows.length !== 1 ? "s" : ""} parsed
                </p>
                <p className="text-xs text-muted-foreground">Headers found: {parsed.headers.join(", ")}</p>
                {parsed.rows.length > 0 && (
                  <div className="text-xs font-mono max-h-40 overflow-y-auto">
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <div key={i} className="truncate">
                        {(r.first_name || "").trim()} {(r.last_name || "").trim()} · {r.email || "—"} · {r.location || "—"} · {r.inquiry_type || "—"}
                      </div>
                    ))}
                    {parsed.rows.length > 5 && <div className="text-muted-foreground">…and {parsed.rows.length - 5} more</div>}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {parsed && parsed.rows.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Step 3 — Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={doImport} disabled={importing} className="w-full sm:w-auto" data-testid="confirm-import-btn">
                <Upload size={14} /> {importing ? "Importing…" : `Import ${parsed.rows.length} lead${parsed.rows.length !== 1 ? "s" : ""}`}
              </Button>
              {result && (
                <div className="space-y-2 mt-4">
                  <p className="text-sm font-medium flex items-center gap-2 text-emerald-500">
                    <CheckCircle2 size={14} /> {result.imported} lead{result.imported !== 1 ? "s" : ""} imported to {String(result.brand).toUpperCase()}
                  </p>
                  {result.skipped?.length > 0 && (
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                      <p className="text-sm font-medium text-amber-500 flex items-center gap-2">
                        <AlertTriangle size={14} /> {result.skipped.length} row{result.skipped.length !== 1 ? "s" : ""} skipped
                      </p>
                      <ul className="text-xs mt-1 text-muted-foreground space-y-0.5">
                        {result.skipped.map((s, i) => (
                          <li key={i}>Row {s.row}: {s.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => navigate("/leads")} size="sm">View Leads →</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
